// src/features/auth/components/PasswordStrengthIndicator.tsx
import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import authService from '../services/authService';
import { PasswordStrengthResponse } from '../types';

interface PasswordStrengthIndicatorProps {
  password: string;
  email?: string;
  showDetails?: boolean;
}

interface PasswordCriteria {
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

export interface PasswordStrength {
  score: number; // 0-5
  level: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong' | 'Invalid';
  color: string;
  criteria: PasswordCriteria[];
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  email = '',
  showDetails = true
}) => {
  const calculatePasswordStrength = (password: string): PasswordStrength => {
    // Helper function to detect sequential characters
    const hasSequentialChars = (pwd: string): boolean => {
      // Check for 3+ consecutive ascending characters (123, abc, ABC)
      for (let i = 0; i < pwd.length - 2; i++) {
        const char1 = pwd.charCodeAt(i);
        const char2 = pwd.charCodeAt(i + 1);
        const char3 = pwd.charCodeAt(i + 2);
        
        if (char2 === char1 + 1 && char3 === char2 + 1) {
          return true;
        }
      }
      
      // Check for 3+ consecutive descending characters (321, cba, CBA)
      for (let i = 0; i < pwd.length - 2; i++) {
        const char1 = pwd.charCodeAt(i);
        const char2 = pwd.charCodeAt(i + 1);
        const char3 = pwd.charCodeAt(i + 2);
        
        if (char2 === char1 - 1 && char3 === char2 - 1) {
          return true;
        }
      }
      
      return false;
    };

    const criteria: PasswordCriteria[] = [
      {
        label: 'At least 8 characters',
        test: (pwd) => pwd.length >= 8,
        met: false
      },
      {
        label: 'Contains uppercase letter',
        test: (pwd) => /[A-Z]/.test(pwd),
        met: false
      },
      {
        label: 'Contains lowercase letter',
        test: (pwd) => /[a-z]/.test(pwd),
        met: false
      },
      {
        label: 'Contains number',
        test: (pwd) => /\d/.test(pwd),
        met: false
      },
      {
        label: 'Contains special character',
        test: (pwd) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
        met: false
      },
      {
        label: 'No sequential characters',
        test: (pwd) => !hasSequentialChars(pwd),
        met: false
      }
    ];

    // Test each criterion
    criteria.forEach(criterion => {
      criterion.met = criterion.test(password);
    });

    const basicScore = criteria.filter(c => c.met).length;
    const hasSequential = hasSequentialChars(password);
    
    let level: PasswordStrength['level'];
    let color: string;
    let score = basicScore;

    // If password has sequential characters, mark as Invalid regardless of other criteria
    if (password.length > 0 && hasSequential) {
      level = 'Invalid';
      color = 'text-red-600 dark:text-red-400';
      score = 0; // Force score to 0 for visual bar
    } else {
      switch (basicScore) {
        case 0:
        case 1:
          level = 'Very Weak';
          color = 'text-red-600 dark:text-red-400';
          break;
        case 2:
          level = 'Weak';
          color = 'text-orange-600 dark:text-orange-400';
          break;
        case 3:
          level = 'Fair';
          color = 'text-yellow-600 dark:text-yellow-400';
          break;
        case 4:
          level = 'Good';
          color = 'text-blue-600 dark:text-blue-400';
          break;
        case 5:
          level = 'Fair'; // 5 criteria met but no sequential check
          color = 'text-yellow-600 dark:text-yellow-400';
          break;
        case 6:
          level = 'Strong'; // All 6 criteria including no sequential
          color = 'text-green-600 dark:text-green-400';
          break;
        default:
          level = 'Very Weak';
          color = 'text-red-600 dark:text-red-400';
      }
    }

    return { score, level, color, criteria };
  };

  const strength = calculatePasswordStrength(password);

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
            Password Strength
          </span>
          <span className={`text-xs font-medium ${strength.color}`}>
            {strength.level}
          </span>
        </div>
        
        <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              strength.score === 0 ? 'bg-red-500' :
              strength.score === 1 ? 'bg-red-500' :
              strength.score === 2 ? 'bg-orange-500' :
              strength.score === 3 ? 'bg-yellow-500' :
              strength.score === 4 ? 'bg-blue-500' :
              'bg-green-500'
            }`}
            style={{ width: `${(strength.score / 6) * 100}%` }}
          />
        </div>
      </div>

      {/* Detailed Criteria */}
      {showDetails && (
        <div className="space-y-1">
          <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
            Password must contain:
          </p>
          <div className="grid grid-cols-1 gap-1">
            {strength.criteria.map((criterion, index) => (
              <div key={index} className="flex items-center space-x-2">
                {criterion.met ? (
                  <CheckCircleIcon className="h-3 w-3 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircleIcon className="h-3 w-3 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
                )}
                <span className={`text-xs ${
                  criterion.met 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-theme-text-tertiary dark:text-dark-theme-text-tertiary'
                }`}>
                  {criterion.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
